import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { TariffButton } from 'widgets/tariff-button'

import findTicketStore from 'entities/beket/models/find-ticket/find-ticket-store'
import formPassengersState from 'entities/beket/models/form-passengers/form-passengers-state.ts'
import listTicketsStore from 'entities/beket/models/ticket/beket-list-tickets/beket-list-tickets-store.ts'
import { FormFooter } from 'entities/beket/ui/order-form/ui/form-footer'
import { setSettingForApp } from 'entities/user/module/app-settings'

import { SearchApiBeket } from 'shared/api/beket/search'
import { ShareKeys } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import ErrorImage from 'shared/images/error.png'
import { checkUserAuth } from 'shared/lib/checkUserAuth.ts'
import { showWrongDateFormat } from 'shared/lib/dateFormat'
import { getErrorMessage } from 'shared/lib/errorText.ts'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { prepareBeketRouteData } from 'shared/lib/prepareBeketRouteData.tsx'
import { prepareDuration } from 'shared/lib/prepareDuration'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import { DetailCard } from './components/detail-card'
import { InfoAboutTrip } from './components/info-about-trip'
import { RideRulesModal } from './components/ride-rules-modal'
import s from './styles.module.scss'

const BeketTripDetails = observer(() => {
  const [loading, setLoading] = useState<boolean>(true)
  const { ticketId } = useParams() as { ticketId: string }

  const { state } = useLocation()

  const currentState = state as { searchKey: string }

  const passengers = findTicketStore.formFindTicket

  const { t } = useTranslation()
  const { openDialog, closeDialog } = useDialogBottomSheet()

  const navigate = useNavigate()

  const ticket = listTicketsStore.selectedTicket

  const onClick = () => {
    openDialog({
      child: <RideRulesModal />,
      isDialog: true,
    })
  }

  const onClickContinue = async () => {
    const navigatePath = RoutingPaths.beket.schemeOrder.path(ticketId)
    const redirectUrl = getRedirectUrl(navigatePath)

    await checkUserAuth({
      redirectUrl,
      onSuccess: () => {
        formPassengersState.resetState()
        void setSettingForApp({ key: ShareKeys.bus, value: '' })

        navigate(navigatePath)
      },
      onError: () => {
        const payload = {
          formFindTicket: findTicketStore.formFindTicket,
          selectedTicket: ticket,
        }
        void setSettingForApp({
          key: ShareKeys.bus,
          value: JSON.stringify(payload),
        })
      },
    })
  }

  const handleTripError = (err: unknown) => {
    const errorText = getErrorMessage(err)

    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorText || t('error_popup_description')}
          button={t('try_again')}
          onClick={closePopup}
        />
      ),
      isDialog: false,
    })
  }

  const getDetailTrip = async () => {
    try {
      setLoading(true)

      const { data: ticketData } = await SearchApiBeket.searchTicketDetail(
        ticketId,
        {
          adult: passengers.adult,
          child: passengers?.child || 0,
          searchkey: currentState?.searchKey,
        },
      )

      listTicketsStore.setDetailTicket(ticketData?.data)
    } catch (err) {
      handleTripError(err)
    } finally {
      setLoading(false)
    }
  }

  const closePopup = () => {
    closeDialog()

    getDetailTrip()
  }

  const onGoBack = () => {
    navigate(RoutingPaths.beket.searchResults.path())
  }

  useEffect(() => {
    void getDetailTrip()
  }, [])

  const routeData = prepareBeketRouteData(ticket)

  const timeDuration = prepareDuration(ticket?.duration)

  const timeDurationText = t('time_flight', {
    hours: timeDuration?.hours,
    minutes: timeDuration?.minutes,
  })

  const countPassengerLength = findTicketStore.getLengthOfPassengers()

  const beketInfo = `${routeData?.departure?.city} –– ${routeData?.arrival?.city}`
  const dateDeparture = showWrongDateFormat(
    ticket?.departure_datetime,
    DATE_FORMAT_LIST.FULL_DATE_BEKET,
  )
  return (
    <>
      <HeaderGoBack
        title={t('beket_content.detail_ride')}
        variant="grey"
        onGoBack={onGoBack}
        buttonWhite
      />

      {loading && <Loader marginTop={150} />}

      {!loading && (
        <div className={clsx('container', s.tripDetails)}>
          <DetailCard
            title={beketInfo}
            routeData={routeData}
            subtitle={`${dateDeparture} • ${timeDurationText}`}
          />

          <InfoAboutTrip
            direction={beketInfo}
            company={ticket?.transport?.autobase}
            route_desc={ticket?.route?.route_desc}
            transportNumber={ticket?.transport?.transport_number}
            transportType={ticket?.transport?.transport_model}
          />

          <TariffButton
            onClick={onClick}
            text={t('beket_content.ride_rules')}
            type="small"
          />

          <FormFooter
            price={ticket?.price || ''}
            count={countPassengerLength}
            onClick={onClickContinue}
          />
        </div>
      )}
    </>
  )
})

export default BeketTripDetails
